import type { Node } from '@/types/node';

export type ExecutePayloadSizeSummary = {
  nodeCount: number;
  modelCount: number;
  dataUrlCount: number;
  dataUrlCharsTotal: number;
  maxDataUrlChars: number;
};

type AxiosLikeError = {
  code?: unknown;
  response?: {
    status?: unknown;
    data?: { message?: unknown };
  };
};

const DATA_URL_PREFIX = 'data:';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getAxiosLike = (error: unknown): AxiosLikeError | undefined => (isRecord(error) ? error : undefined);

/** Walk without concatenating strings — length only, cycles skipped. */
const visitDataUrls = (value: unknown, seen: WeakSet<object>, summary: ExecutePayloadSizeSummary): void => {
  if (typeof value === 'string') {
    if (value.startsWith(DATA_URL_PREFIX)) {
      summary.dataUrlCount += 1;
      summary.dataUrlCharsTotal += value.length;
      summary.maxDataUrlChars = Math.max(summary.maxDataUrlChars, value.length);
    }
    return;
  }
  if (typeof value !== 'object' || value === null) {
    return;
  }
  if (seen.has(value)) {
    return;
  }
  seen.add(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      visitDataUrls(item, seen, summary);
    }
    return;
  }
  for (const nested of Object.values(value)) {
    visitDataUrls(nested, seen, summary);
  }
};

export const summarizeExecutePayloadSize = (
  nodes: Node[],
  extras?: { computedImages?: unknown },
): ExecutePayloadSizeSummary => {
  const summary: ExecutePayloadSizeSummary = {
    nodeCount: nodes.length,
    modelCount: nodes.filter((node) => node.isModel).length,
    dataUrlCount: 0,
    dataUrlCharsTotal: 0,
    maxDataUrlChars: 0,
  };
  visitDataUrls(nodes, new WeakSet(), summary);
  if (extras?.computedImages !== undefined) {
    visitDataUrls(extras.computedImages, new WeakSet(), summary);
  }
  return summary;
};

export const isOversizedExecutePayloadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  if (/invalid string length|string too long/i.test(message)) {
    return true;
  }

  const axiosLike = getAxiosLike(error);
  if (axiosLike?.response?.status === 413) {
    return true;
  }
  if (axiosLike?.code === 'ERR_FR_MAX_BODY_LENGTH_EXCEEDED') {
    return true;
  }
  const responseMessage = axiosLike?.response?.data?.message;
  return typeof responseMessage === 'string' && /payload too large|entity too large/i.test(responseMessage);
};

export type ClientPayloadFailurePhase = 'client_prepare' | 'client_stringify' | 'client_http';

export const getClientPayloadFailurePhase = (error: unknown, snapshotCompleted: boolean): ClientPayloadFailurePhase => {
  if (error instanceof Error && error.name === 'AxiosError') {
    return 'client_http';
  }
  const axiosLike = getAxiosLike(error);
  if (typeof axiosLike?.response?.status === 'number' || typeof axiosLike?.code === 'string') {
    return 'client_http';
  }
  return snapshotCompleted ? 'client_stringify' : 'client_prepare';
};

/** Enumerable fields only — never pass the Error/AxiosError (config.data can be the full body). */
export const serializeExecuteErrorForLog = (error: unknown): Record<string, unknown> => {
  const fields: Record<string, unknown> = {};
  if (error instanceof Error) {
    fields.errorName = error.name;
    fields.errorMessage = error.message;
  } else {
    fields.errorMessage = String(error);
  }

  const axiosLike = getAxiosLike(error);
  if (typeof axiosLike?.response?.status === 'number') {
    fields.axiosStatus = axiosLike.response.status;
  }
  if (typeof axiosLike?.code === 'string') {
    fields.axiosCode = axiosLike.code;
  }
  const responseMessage = axiosLike?.response?.data?.message;
  if (typeof responseMessage === 'string') {
    fields.axiosResponseMessage = responseMessage.slice(0, 200);
  }
  return fields;
};
